import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';

const KPICard = ({
  title,
  value,
  subtitle,
  trend,
  trendValue,
  icon: Icon,
  color = 'primary',
  size = 'default',
  tooltip,
  loading = false,
  className = ''
}) => {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600 border-primary-200',
    success: 'bg-green-50 text-green-600 border-green-200',
    warning: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    danger: 'bg-red-50 text-red-600 border-red-200',
    info: 'bg-blue-50 text-blue-600 border-blue-200',
    neutral: 'bg-gray-50 text-gray-600 border-gray-200'
  };

  const sizeClasses = {
    small: 'p-4',
    default: 'p-6',
    large: 'p-8'
  };

  const formatValue = (val) => {
    if (loading) return '...';
    if (val === null || val === undefined) return '-';

    // Si es un número
    if (typeof val === 'number') {
      if (val >= 1000000) {
        return `${(val / 1000000).toFixed(1)}M`;
      }
      if (val >= 1000) {
        return `${(val / 1000).toFixed(1)}K`;
      }
      return val % 1 === 0 ? val.toString() : val.toFixed(2);
    }

    return val.toString();
  };

  const getTrendIcon = () => {
    if (!trend || loading) return null;

    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTrendColor = () => {
    if (!trend) return 'text-gray-500';
    return trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-gray-500';
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      className={`
        relative bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-600
        shadow-sm hover:shadow-md transition-all duration-200 group
        ${sizeClasses[size]} ${className}
      `}
    >
      {/* Icon Background */}
      {Icon && (
        <div className={`
          inline-flex items-center justify-center w-12 h-12 rounded-lg border mb-4
          ${colorClasses[color]}
        `}>
          <Icon className="w-6 h-6" />
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="relative group/tooltip">
            <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
            <div className="absolute bottom-full right-0 mb-2 hidden group-hover/tooltip:block">
              <div className="bg-gray-900 text-white text-sm rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                {tooltip}
                <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Title */}
      <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 pr-8">
        {title}
      </h3>

      {/* Main Value */}
      <div className="flex items-baseline space-x-2 mb-2">
        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {loading ? (
            <div className="animate-pulse bg-gray-200 dark:bg-gray-700 h-8 w-20 rounded"></div>
          ) : (
            formatValue(value)
          )}
        </span>

        {/* Trend */}
        {(trend || trendValue) && !loading && (
          <div className="flex items-center space-x-1">
            {getTrendIcon()}
            {trendValue && (
              <span className={`text-sm font-medium ${getTrendColor()}`}>
                {typeof trendValue === 'number' ? `${trendValue > 0 ? '+' : ''}${trendValue}%` : trendValue}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Subtitle */}
      {subtitle && !loading && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {subtitle}
        </p>
      )}

      {/* Loading State */}
      {loading && (
        <div className="space-y-2">
          <div className="animate-pulse bg-gray-200 dark:bg-gray-700 h-4 w-24 rounded"></div>
        </div>
      )}
    </motion.div>
  );
};

export default KPICard;